import React, { useCallback, useEffect, useRef, useState } from "react";
import { Camera, Loader2, MapPin, RefreshCw, ShieldCheck } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "src/components/ui/dialog";
import { Button } from "src/components/ui/button";
import { useToast } from "src/components/ui/toast";
import { useAuth } from "src/contexts/AuthContext";
import { checkInSiteVisit } from "src/features/site_visits/api/siteVisitsApi";

// Geofenced check-in for a scheduled site visit. Captures device location +
// a selfie, posts to the server (the authoritative geofence gate), and surfaces
// verified / out-of-range results. A user with site_visits:configure may force
// a manual override when location can't be verified.
export function CheckInDialog({ open, onOpenChange, visit, onCheckedIn }) {
  const { toast } = useToast();
  const { can } = useAuth();
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const [position, setPosition] = useState(null);
  const [locating, setLocating] = useState(false);
  const [selfie, setSelfie] = useState(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const canOverride = can("site_visits", "configure");

  const locate = useCallback(() => {
    if (!navigator.geolocation) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setPosition({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy });
        setLocating(false);
      },
      () => setLocating(false),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  }, []);

  const stopCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setCameraReady(false);
  }, []);

  const startCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" }, audio: false });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setCameraReady(true);
    } catch {
      setCameraReady(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      locate();
      startCamera();
    } else {
      stopCamera();
      if (selfie?.url) URL.revokeObjectURL(selfie.url);
      setSelfie(null);
      setPosition(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function capture() {
    const video = videoRef.current;
    if (!video) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);
    canvas.toBlob(
      (blob) => {
        if (blob) {
          setSelfie({ blob, url: URL.createObjectURL(blob) });
          stopCamera();
        }
      },
      "image/jpeg",
      0.85
    );
  }

  function retake() {
    if (selfie?.url) URL.revokeObjectURL(selfie.url);
    setSelfie(null);
    startCamera();
  }

  async function submit({ override = false } = {}) {
    if (!override && !position) {
      toast({ variant: "destructive", title: "Location needed", description: "Enable location to check in." });
      return;
    }
    setSubmitting(true);
    try {
      const res = await checkInSiteVisit(visit.id, {
        latitude: position?.lat,
        longitude: position?.lng,
        accuracyMeters: position?.accuracy,
        selfieBlob: selfie?.blob,
        override,
      });
      toast({
        title: res.status === "manual_override" ? "Checked in (override)" : "Checked in",
        description: "Visit marked as checked in.",
      });
      onCheckedIn?.(res.site_visit);
      onOpenChange(false);
    } catch (error) {
      const data = error.response?.data;
      toast({
        variant: "destructive",
        title: data?.status === "out_of_range" ? "Out of range" : "Couldn't check in",
        description: data?.message || "Something went wrong.",
      });
      // If the server rejected for range and the visit row was still updated,
      // refresh it so the UI reflects the recorded attempt.
      if (data?.site_visit) onCheckedIn?.(data.site_visit);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Check in to site visit</DialogTitle>
        </DialogHeader>

        <div className="aspect-[4/3] w-full overflow-hidden rounded-lg border border-slate-200 bg-slate-100">
          {selfie ? (
            <img src={selfie.url} alt="Check-in" className="h-full w-full object-cover" />
          ) : (
            <video ref={videoRef} className="h-full w-full object-cover" muted playsInline />
          )}
        </div>

        <div className="flex items-center gap-1.5 text-[12px] text-slate-500">
          {locating ? (
            <>
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Locating you…
            </>
          ) : position ? (
            <>
              <MapPin className="h-3.5 w-3.5 text-emerald-600" /> Location captured
              {position.accuracy ? ` (±${Math.round(position.accuracy)} m)` : ""}
            </>
          ) : (
            <>
              <MapPin className="h-3.5 w-3.5 text-rose-500" /> Location unavailable
            </>
          )}
        </div>

        <div className="flex gap-2">
          {selfie ? (
            <>
              <Button variant="outline" className="flex-1" onClick={retake} disabled={submitting}>
                <RefreshCw className="mr-1.5 h-4 w-4" /> Retake
              </Button>
              <Button className="flex-1" onClick={() => submit()} disabled={submitting || !position}>
                {submitting && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
                Check in
              </Button>
            </>
          ) : (
            <Button className="flex-1" onClick={capture} disabled={!cameraReady}>
              <Camera className="mr-1.5 h-4 w-4" /> Capture photo
            </Button>
          )}
        </div>

        {canOverride && (
          <button
            type="button"
            onClick={() => submit({ override: true })}
            disabled={submitting}
            className="mt-1 flex items-center justify-center gap-1.5 text-[12px] font-medium text-slate-400 hover:text-slate-600"
          >
            <ShieldCheck className="h-3.5 w-3.5" /> Override &amp; check in without location
          </button>
        )}
      </DialogContent>
    </Dialog>
  );
}
